/**
 * report 命令：报表运维（list / get / rename / delete / patch / create-from-xml）。
 * list：列出租户下报表（递归遍历目录树）。
 *   底层走 /api/packet/loadChildren（parentId + recursive）：服务端部署 recursive 透传后
 *   一次返回完整子树；未部署时自动退化为逐层下钻。patch：读取已有报表 → 局部修改 → 回写，
 *   避免重复创建产生垃圾报表。
 */

import { readFileSync, writeFileSync } from 'node:fs';
import { Command } from 'commander';
import { login, resolveTenantOrSelect } from '../client/session.js';
import { requireAuth, resolveAuth, applyConfigDir } from '../core/config.js';
import {
  loadReportXml,
  renameReport,
  removeReport,
  createReport,
  updateReportXml,
  publishReport,
  listAllReportFiles,
  listReportFiles,
} from '../client/fileApi.js';
import { designerUrl, viewUrl } from '../client/urls.js';
import { parseReport } from '../core/xml/parse.js';

export function registerReport(program: Command) {
  const r = program.command('report').description('报表文件运维');

  const auth = (cmd: Command) =>
    cmd
      .option('--base-url <url>', '服务端地址（缺省读 BASK_BASE_URL / .env）')
      .option('--username <user>', '登录用户名（Session 登录；与 --access-token 互斥，缺省读 BASK_USERNAME / .env）')
      .option('--password <pwd>', '登录密码（Session 登录；与 --access-token 互斥，缺省读 BASK_PASSWORD / .env）')
      .option('--access-token <token>', 'OAuth2 访问令牌（与 username/password 互斥；缺省读 BASK_ACCESS_TOKEN / .env）')
      .option('--access-token-header <name>', 'OAuth2 token 注入的 header 名，缺省 authorization（值形如 Bearer <token>）；缺省读 BASK_ACCESS_TOKEN_HEADER / .env')
      .option('--config-dir <dir>', '配置查找目录（含 .env / baskreport.config.json）')
      .option('--tenantId <id>', '租户 ID（可省略；省略时登录后自动解析唯一租户，多租户需显式指定）');

  const loginResolved = async (opts: any, ctx: string) => {
    applyConfigDir(opts);
    const resolved = resolveAuth(opts);
    requireAuth(resolved, ctx);
    const s = await login(resolved);
    // tenantId 未显式提供时，登录后调用 user/get 自动解析；多租户则引导用户（LLM 呈现候选 / TTY 交互）选择。
    s.tenantId = await resolveTenantOrSelect(s, resolved.tenantId);
    return s;
  };

  auth(
    r
      .command('get')
      .description('读取报表 XML 源')
      .requiredOption('--id <id>', '报表文件 ID')
      .option('--as-json', '将 XML 解析为 ReportIR（JSON 中间层）输出，而非原始 XML'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    const xml = await loadReportXml(s, Number(opts.id));
    if (opts.asJson) {
      const ir = parseReport(xml);
      if (!ir) {
        throw new Error(
          '该报表根节点不是 <report>（可能是 <dashboard> 仪表盘/图表），不在 IR 承载范围内，无法转 JSON。请去掉 --as-json 直接获取 XML。',
        );
      }
      console.log(JSON.stringify(ir, null, 2));
    } else {
      console.log(xml);
    }
  });

  // 列举报表：从指定父目录递归遍历整棵文件树，收集全部 type==='report' 的报表。
  auth(
    r
      .command('list')
      .description('列出租户下的报表文件（递归遍历目录树）')
      .option('--parent-id <id>', '起始父目录 ID，缺省 0（根目录）；显式指定时只列该目录一层（loadChildren?parentId&recursive=false）')
      .option('--type <type>', '文件类型过滤，缺省 report（仅列报表）', 'report')
      .option('--limit <n>', '最大返回条数，达到即停止递归（缓解报表过多时 token 膨胀）；缺省不限制')
      .option('--json', '仅输出 JSON（缺省额外打印紧凑列表）'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    const parentId = opts.parentId != null ? Number(opts.parentId) : 0;
    const limit = opts.limit != null ? Number(opts.limit) : undefined;
    // --parent-id 显式指定时只列该目录一层；否则递归拉全租户内所有报表。
    const files = await (opts.parentId != null
      ? listReportFiles(s, parentId)
      : listAllReportFiles(s, { parentId, type: opts.type, limit }));
    const reports = opts.parentId != null
      ? files.filter((f: any) => (opts.type ? f.type === opts.type : true))
      : files;
    // 触发 limit 截断时提示用户：结果为子集，应缩小范围（--parent-id / --limit 调大 / --name）再取。
    const truncated = limit != null && reports.length >= limit;
    console.log(JSON.stringify(reports, null, 2));
    if (!opts.json) {
      for (const f of reports) {
        const code = f.code ? ` [${f.code}]` : '';
        const designer = designerUrl(s.baseUrl, f.id, s.tenantId);
        console.log(`  ${f.id}\t${f.name}${code}\t${designer}`);
      }
      const typeLabel = opts.type === 'report' ? '报表' : opts.type;
      if (truncated) {
        console.log(
          `\n已截断：仅返回前 ${reports.length} 个${typeLabel}（达到 --limit）。结果不完整，` +
            `请用 --parent-id <目录ID> 缩小范围，或调大 --limit 后重试。`,
        );
      } else {
        console.log(`\n共 ${reports.length} 个${typeLabel}文件。`);
      }
    }
  });

  auth(
    r
      .command('rename')
      .description('重命名报表')
      .requiredOption('--id <id>', '报表文件 ID')
      .requiredOption('--name <name>', '新名称'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    await renameReport(s, Number(opts.id), opts.name);
    console.log('已重命名');
  });

  auth(
    r.command('delete').description('删除报表').requiredOption('--id <id>', '报表文件 ID'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    await removeReport(s, Number(opts.id));
    console.log('已删除');
  });

  auth(
    r
      .command('patch')
      .description('用新的 XML 文件覆盖更新已有报表（避免重复创建）')
      .requiredOption('--id <id>', '报表文件 ID')
      .requiredOption('--xml <file>', '新 XML 文件'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    const id = Number(opts.id);
    // 修改前先发布当前版本：让后台把线上内容封存为可回滚快照，再覆盖新内容。
    // 若该报表从未部署过（无可快照版本），publishReport 返回 false，静默跳过。
    const published = await publishReport(s, id).catch((e) => {
      console.warn(`  [跳过] 修改前发布当前版本失败：${e instanceof Error ? e.message : e}（将继续更新）`);
      return false;
    });
    if (published) console.log('已发布当前版本（用于后续可恢复）：');
    const xml = readFileSync(opts.xml, 'utf-8');
    await updateReportXml(s, id, xml);
    console.log('已更新：');
    console.log(`  设计器: ${designerUrl(s.baseUrl, opts.id, s.tenantId)}`);
    console.log(`  预览:   ${viewUrl(s.baseUrl, opts.id, s.tenantId)}`);
  });

  // 便捷：本地 XML 直接创建为报表
  auth(
    r
      .command('create-from-xml')
      .description('由本地 XML 文件创建报表（code 留空，后续以 --id 定位）')
      .requiredOption('--xml <file>', 'XML 文件')
      .option('--name <name>', '报表名称')
      .option('--parent-id <id>', '父目录 ID，缺省 0'),
  ).action(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    const xml = readFileSync(opts.xml, 'utf-8');
    const file = await createReport(s, {
      parentId: opts.parentId ? Number(opts.parentId) : 0,
      name: opts.name ?? 'AI 生成的报表',
    });
    await updateReportXml(s, file.id, xml);
    console.log(`  设计器: ${designerUrl(s.baseUrl, file.id, s.tenantId)}`);
    console.log(`  预览:   ${viewUrl(s.baseUrl, file.id, s.tenantId)}`);
  });
}
